import { useState, type ReactElement } from 'react'
import {
  ArrowDownUp,
  Cpu,
  Network as NetworkIcon,
  RadioTower,
  Router,
  RotateCcw,
  Signal,
  Unplug,
  Wifi,
  Wrench,
} from 'lucide-react'
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Dot, WanBadge } from '@/components/FreeboxCard'
import { DeviceFaceplate } from '@/components/network/DeviceFaceplate'
import { SfpTempChart } from '@/components/network/SfpTempChart'
import { TopologyBand } from '@/components/network/TopologyBand'
import { relative } from '@/lib/format'
import {
  dbm,
  mbps,
  uptime,
  useFreebox,
  useFreeboxEvents,
  useFreeboxLanHosts,
  useFreeboxSeries,
  useFreeboxWifi,
  type FreeboxEvent,
} from '@/lib/freebox'
import { pivotSeries, useMikrotik, useMikrotikSeries } from '@/lib/mikrotik'

const RANGES = ['24h', '7d', '30d', '90d'] as const
type Range = (typeof RANGES)[number]

const AXIS_TICK = { fontSize: 11, fill: 'hsl(var(--muted-foreground))' }
const TOOLTIP_STYLE = {
  background: 'hsl(var(--card))',
  border: '1px solid hsl(var(--border))',
  borderRadius: 8,
  fontSize: 12,
}

function tsLabel(v: unknown): string {
  return typeof v === 'string' ? new Date(v).toLocaleString() : String(v)
}

function ChartFrame({ children }: { children: ReactElement }) {
  return (
    <div style={{ width: '100%', height: 220 }}>
      <ResponsiveContainer>{children}</ResponsiveContainer>
    </div>
  )
}

const EVENT_META: Record<
  FreeboxEvent['kind'],
  { icon: typeof Signal; label: (e: FreeboxEvent) => string; tone: string }
> = {
  media: {
    icon: RadioTower,
    label: (e) =>
      e.to === 'backup_4g'
        ? 'Bascule sur backup 4G (fibre perdue)'
        : `Retour ${e.to} (fin du backup ${e.from})`,
    tone: 'text-amber-500',
  },
  sfp_link: {
    icon: Unplug,
    label: (e) => (e.to === 'false' ? 'Lien optique perdu' : 'Lien optique rétabli'),
    tone: 'text-destructive',
  },
  firmware: {
    icon: Wrench,
    label: (e) => `Firmware ${e.from} → ${e.to}`,
    tone: 'text-primary',
  },
  reboot: {
    icon: RotateCcw,
    label: () => 'Reboot de la box (uptime remis à zéro)',
    tone: 'text-muted-foreground',
  },
}

function episodeDuration(e: FreeboxEvent, all: FreeboxEvent[]): string | null {
  // Opening events: media->backup_4g and sfp_link->false. The closing event is
  // the next event of the same kind, later in time (array is ts-desc).
  const opening =
    (e.kind === 'media' && e.to === 'backup_4g') ||
    (e.kind === 'sfp_link' && e.to === 'false')
  if (!opening) return null
  const later = all
    .filter((x) => x.kind === e.kind && x.ts > e.ts)
    .sort((a, b) => a.ts.localeCompare(b.ts))[0]
  if (!later) return 'en cours'
  const mins = Math.round(
    (new Date(later.ts).getTime() - new Date(e.ts).getTime()) / 60000,
  )
  if (mins < 60) return `${mins} min`
  return `${Math.floor(mins / 60)} h ${String(mins % 60).padStart(2, '0')}`
}

export function NetworkPage() {
  const [range, setRange] = useState<Range>('24h')
  const { data, isLoading } = useFreebox()
  const { data: series } = useFreeboxSeries(range)
  const { data: eventsData } = useFreeboxEvents(range)
  const { data: lan } = useFreeboxLanHosts()
  const { data: wifi } = useFreeboxWifi(range)
  const { data: mikrotik } = useMikrotik()
  const { data: mikrotikSeries } = useMikrotikSeries(range)
  const [highlightedDevice, setHighlightedDevice] = useState<string | null>(null)

  if (isLoading) return <Skeleton className="h-96" />
  const box = data?.boxes[0]
  const pivot = mikrotikSeries ? pivotSeries(mikrotikSeries) : null

  const points = (series?.points ?? []).map((p) => ({
    t: p.b,
    down: p.rate_down_bytes_s != null ? (p.rate_down_bytes_s * 8) / 1e6 : null,
    up: p.rate_up_bytes_s != null ? (p.rate_up_bytes_s * 8) / 1e6 : null,
    rx: p.sfp_pwr_rx_dbm,
    tx: p.sfp_pwr_tx_dbm,
    cpu: p.temp_cpub_c,
    t1: p.temp_t1_c,
    t2: p.temp_t2_c,
    fan: p.fan_rpm,
  }))
  const events = eventsData?.events ?? []
  const hosts = lan?.hosts ?? []
  const activeCount = hosts.filter((h) => h.active).length
  const threshold = box?.sfp_stats?.rx_threshold_dbm ?? -28

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight">Network</h1>
        <div className="flex gap-1">
          {RANGES.map((r) => (
            <Button
              key={r}
              size="sm"
              variant={r === range ? 'default' : 'outline'}
              onClick={() => setRange(r)}
            >
              {r}
            </Button>
          ))}
        </div>
      </div>

      {!box && (
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground">
            Aucune Freebox ne remonte de données (rôle non déployé, pairing absent,
            ou box injoignable depuis plus de 10 minutes).
          </CardContent>
        </Card>
      )}

      {box && (
        <Card>
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Signal className="h-4 w-4 text-primary" /> WAN — {box.source}
            </CardTitle>
            <WanBadge box={box} />
          </CardHeader>
          <CardContent className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
            <div>
              <div className="text-xs text-muted-foreground">Débit ↓ / ↑</div>
              <div className="font-semibold tabular-nums">
                {mbps(box.rate_down_bytes_s)} / {mbps(box.rate_up_bytes_s)}
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Bande passante</div>
              <div className="font-semibold tabular-nums">
                {box.bandwidth_down_bps != null
                  ? `${(box.bandwidth_down_bps / 1e9).toFixed(1)} Gb/s`
                  : '—'}{' '}
                /{' '}
                {box.bandwidth_up_bps != null
                  ? `${(box.bandwidth_up_bps / 1e6).toFixed(0)} Mb/s`
                  : '—'}
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">IPv4</div>
              <div className="font-semibold tabular-nums">{box.ipv4 ?? '—'}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Uptime box</div>
              <div className="font-semibold tabular-nums">{uptime(box.uptime_s)}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Firmware</div>
              <div className="font-semibold tabular-nums">
                {box.firmware_version ?? '—'}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Router className="h-4 w-4 text-primary" /> Équipements — routeur & switch
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!mikrotik || mikrotik.devices.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Aucun équipement MikroTik ne remonte de données (rôle non déployé,
              credentials absents, ou équipements injoignables depuis plus d'une heure).
            </p>
          ) : (
            <>
              <TopologyBand
                data={mikrotik}
                freebox={box}
                onSelectDevice={(name) => {
                  setHighlightedDevice(name)
                  document
                    .getElementById(`faceplate-${name}`)
                    ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
                }}
              />
              <div className="space-y-4">
                {mikrotik.devices.map((d) => (
                  <DeviceFaceplate
                    key={d.device}
                    id={`faceplate-${d.device}`}
                    device={d}
                    thresholds={mikrotik.temp_thresholds}
                    highlighted={highlightedDevice === d.device}
                  />
                ))}
              </div>
              {pivot && pivot.rows.length > 1 && (
                <ChartFrame>
                  <SfpTempChart
                    pivot={pivot}
                    thresholds={mikrotik.temp_thresholds}
                    axisTick={AXIS_TICK}
                    tooltipStyle={TOOLTIP_STYLE}
                    tsLabel={tsLabel}
                  />
                </ChartFrame>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Unplug className="h-4 w-4 text-primary" /> SFP / Fibre
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {box && (
            <>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                <Dot label="présent" ok={box.sfp_present} />
                <Dot label="alim" ok={box.sfp_alim_ok} />
                <Dot label="signal optique" ok={box.sfp_has_signal} />
                <Dot label="link" ok={box.sfp_link} />
                <Dot label="power report" ok={box.sfp_has_power_report} />
              </div>
              <div className="text-xs text-muted-foreground">
                {box.sfp_vendor ?? '—'} {box.sfp_model ?? ''} · s/n {box.sfp_serial ?? '—'} ·
                Rx {dbm(box.sfp_pwr_rx_dbm)} · Tx {dbm(box.sfp_pwr_tx_dbm)}
                {box.sfp_pwr_rx_dbm != null && (
                  <>
                    {' '}· marge{' '}
                    <span
                      className={
                        box.sfp_pwr_rx_dbm - threshold < 2
                          ? 'text-amber-500 font-medium'
                          : 'text-emerald-500 font-medium'
                      }
                    >
                      {(box.sfp_pwr_rx_dbm - threshold).toFixed(1)} dB
                    </span>
                  </>
                )}
              </div>
              {box.sfp_stats && (
                <div className="text-xs text-muted-foreground">
                  Rx 24h min {dbm(box.sfp_stats.rx_min_24h)} / moy {dbm(box.sfp_stats.rx_avg_24h)} / max{' '}
                  {dbm(box.sfp_stats.rx_max_24h)} · link flaps 24h : {box.sfp_stats.link_flaps_24h}
                  {box.sfp_stats.backup_pct_24h != null && (
                    <>
                      {' '}· backup 4G : {box.sfp_stats.backup_pct_24h}% (24h)
                      {box.sfp_stats.backup_pct_7d != null && <> / {box.sfp_stats.backup_pct_7d}% (7j)</>}
                    </>
                  )}
                </div>
              )}
            </>
          )}
          {points.length > 1 ? (
            <ChartFrame>
              <LineChart data={points} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="t"
                  tick={AXIS_TICK}
                  tickFormatter={(v) =>
                    typeof v === 'string' ? new Date(v).toLocaleString() : String(v)
                  }
                  stroke="hsl(var(--muted-foreground))"
                />
                <YAxis
                  tick={AXIS_TICK}
                  tickFormatter={(v) => `${v} dBm`}
                  stroke="hsl(var(--muted-foreground))"
                  width={70}
                  domain={['auto', 'auto']}
                />
                <Tooltip contentStyle={TOOLTIP_STYLE} labelFormatter={tsLabel} />
                <ReferenceLine
                  y={threshold}
                  stroke="hsl(var(--destructive))"
                  strokeDasharray="6 4"
                  label={{ value: `seuil ${threshold} dBm`, fontSize: 10, fill: 'hsl(var(--destructive))' }}
                />
                <Line type="monotone" dataKey="rx" name="Rx (dBm)" stroke="hsl(var(--primary))" dot={false} strokeWidth={2} connectNulls />
                <Line type="monotone" dataKey="tx" name="Tx (dBm)" stroke="hsl(var(--muted-foreground))" dot={false} strokeWidth={1} strokeDasharray="2 4" connectNulls />
              </LineChart>
            </ChartFrame>
          ) : (
            <p className="text-sm text-muted-foreground">Pas encore de données sur la période.</p>
          )}
          <div>
            <div className="text-xs font-medium mb-2">
              Incidents ({range}) — {events.length} événement{events.length > 1 ? 's' : ''}
              {events.length >= 500 && (
                <span className="text-muted-foreground"> (tronqué aux 500 plus récents)</span>
              )}
            </div>
            {events.length === 0 && (
              <p className="text-sm text-muted-foreground">Aucun incident sur la période.</p>
            )}
            <div className="space-y-1">
              {events.map((e, i) => {
                const meta = EVENT_META[e.kind]
                const Icon = meta.icon
                const dur = episodeDuration(e, events)
                return (
                  <div key={`${e.ts}-${e.kind}-${i}`} className="flex items-center gap-2 text-sm">
                    <Icon className={`h-4 w-4 shrink-0 ${meta.tone}`} />
                    <span>{meta.label(e)}</span>
                    {dur && <span className="text-xs text-amber-500">({dur})</span>}
                    <span className="ml-auto text-xs text-muted-foreground whitespace-nowrap">
                      {new Date(e.ts).toLocaleString()} · {relative(e.ts)}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ArrowDownUp className="h-4 w-4 text-primary" /> Trafic WAN
          </CardTitle>
        </CardHeader>
        <CardContent>
          {points.length > 1 ? (
            <ChartFrame>
              <LineChart data={points} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="t"
                  tick={AXIS_TICK}
                  tickFormatter={(v) =>
                    typeof v === 'string' ? new Date(v).toLocaleString() : String(v)
                  }
                  stroke="hsl(var(--muted-foreground))"
                />
                <YAxis
                  tick={AXIS_TICK}
                  tickFormatter={(v) => `${Math.round(Number(v))} Mb/s`}
                  stroke="hsl(var(--muted-foreground))"
                  width={80}
                />
                <Tooltip contentStyle={TOOLTIP_STYLE} labelFormatter={tsLabel} />
                <Line type="monotone" dataKey="down" name="Down (Mb/s)" stroke="hsl(var(--primary))" dot={false} strokeWidth={2} connectNulls />
                <Line type="monotone" dataKey="up" name="Up (Mb/s)" stroke="hsl(var(--muted-foreground))" dot={false} strokeWidth={1.5} connectNulls />
              </LineChart>
            </ChartFrame>
          ) : (
            <p className="text-sm text-muted-foreground">Pas encore de données sur la période.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Cpu className="h-4 w-4 text-primary" /> Box — températures & ventilation
          </CardTitle>
        </CardHeader>
        <CardContent>
          {points.length > 1 ? (
            <ChartFrame>
              <LineChart data={points} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="t"
                  tick={AXIS_TICK}
                  tickFormatter={(v) =>
                    typeof v === 'string' ? new Date(v).toLocaleString() : String(v)
                  }
                  stroke="hsl(var(--muted-foreground))"
                />
                <YAxis
                  yAxisId="temp"
                  tick={AXIS_TICK}
                  tickFormatter={(v) => `${v} °C`}
                  stroke="hsl(var(--muted-foreground))"
                  width={60}
                />
                <YAxis
                  yAxisId="fan"
                  orientation="right"
                  tick={AXIS_TICK}
                  tickFormatter={(v) => `${v} rpm`}
                  stroke="hsl(var(--muted-foreground))"
                  width={70}
                />
                <Tooltip contentStyle={TOOLTIP_STYLE} labelFormatter={tsLabel} />
                <Line yAxisId="temp" type="monotone" dataKey="cpu" name="CPU SoC (°C)" stroke="hsl(var(--destructive))" dot={false} strokeWidth={2} connectNulls />
                <Line yAxisId="temp" type="monotone" dataKey="t1" name="Carte T1 (°C)" stroke="hsl(var(--primary))" dot={false} strokeWidth={1.5} connectNulls />
                <Line yAxisId="temp" type="monotone" dataKey="t2" name="Carte T2 (°C)" stroke="hsl(var(--muted-foreground))" dot={false} strokeWidth={1.5} connectNulls />
                <Line yAxisId="fan" type="monotone" dataKey="fan" name="Ventilateur (rpm)" stroke="hsl(var(--muted-foreground))" dot={false} strokeWidth={1} strokeDasharray="4 4" connectNulls />
              </LineChart>
            </ChartFrame>
          ) : (
            <p className="text-sm text-muted-foreground">Pas encore de données sur la période.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Wifi className="h-4 w-4 text-primary" /> Wi-Fi
          </CardTitle>
          {wifi && wifi.bss.length > 0 && (
            <span className="text-xs text-muted-foreground">
              {wifi.bss.reduce((n, b) => n + (b.sta_count ?? 0), 0)} station
              {wifi.bss.reduce((n, b) => n + (b.sta_count ?? 0), 0) > 1 ? 's' : ''} connectée
              {wifi.bss.reduce((n, b) => n + (b.sta_count ?? 0), 0) > 1 ? 's' : ''}
            </span>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {!wifi || wifi.bss.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Pas de données Wi-Fi (radio coupée ou box injoignable).
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {wifi.bss.map((b) => (
                <div key={b.bss_id} className="flex items-center gap-3 text-sm">
                  <span
                    className={`inline-block h-2 w-2 rounded-full ${
                      b.state === 'active' && b.enabled
                        ? 'bg-emerald-500'
                        : 'bg-muted-foreground/40'
                    }`}
                  />
                  <span className="font-medium">{b.ssid ?? '—'}</span>
                  <span className="text-xs text-muted-foreground">{b.band ?? '—'}</span>
                  <span className="ml-auto text-xs text-muted-foreground">
                    {b.sta_count ?? 0} station{(b.sta_count ?? 0) > 1 ? 's' : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
          {(wifi?.points.length ?? 0) > 1 && (
            <ChartFrame>
              <LineChart data={wifi!.points} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="b"
                  tick={AXIS_TICK}
                  tickFormatter={(v) =>
                    typeof v === 'string' ? new Date(v).toLocaleString() : String(v)
                  }
                  stroke="hsl(var(--muted-foreground))"
                />
                <YAxis
                  tick={AXIS_TICK}
                  allowDecimals={false}
                  stroke="hsl(var(--muted-foreground))"
                  width={40}
                />
                <Tooltip contentStyle={TOOLTIP_STYLE} labelFormatter={tsLabel} />
                <Line type="monotone" dataKey="sta_count_2g4" name="2.4 GHz" stroke="hsl(var(--primary))" dot={false} strokeWidth={1.5} connectNulls />
                <Line type="monotone" dataKey="sta_count_5g" name="5 GHz" stroke="hsl(var(--muted-foreground))" dot={false} strokeWidth={1.5} connectNulls />
              </LineChart>
            </ChartFrame>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <NetworkIcon className="h-4 w-4 text-primary" /> Appareils LAN
          </CardTitle>
          <span className="text-xs text-muted-foreground">
            {activeCount} actif{activeCount > 1 ? 's' : ''} / {hosts.length} connu
            {hosts.length > 1 ? 's' : ''}
          </span>
        </CardHeader>
        <CardContent>
          {hosts.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Inventaire vide — le rôle freebox le remplit toutes les 5 minutes.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b border-border">
                    <th className="py-2 pr-3 font-medium">État</th>
                    <th className="py-2 pr-3 font-medium">Nom</th>
                    <th className="py-2 pr-3 font-medium">MAC</th>
                    <th className="py-2 pr-3 font-medium">Vendor</th>
                    <th className="py-2 pr-3 font-medium">Type</th>
                    <th className="py-2 pr-3 font-medium">IPs</th>
                    <th className="py-2 font-medium">Dernière activité</th>
                  </tr>
                </thead>
                <tbody>
                  {hosts.map((h) => (
                    <tr key={h.mac} className="border-b border-border/50">
                      <td className="py-1.5 pr-3">
                        <span
                          className={`inline-block h-2 w-2 rounded-full ${
                            h.active ? 'bg-emerald-500' : 'bg-muted-foreground/40'
                          }`}
                        />
                      </td>
                      <td className="py-1.5 pr-3 font-medium">{h.name ?? '—'}</td>
                      <td className="py-1.5 pr-3 font-mono text-xs">{h.mac}</td>
                      <td className="py-1.5 pr-3 text-muted-foreground">{h.vendor ?? '—'}</td>
                      <td className="py-1.5 pr-3 text-muted-foreground">{h.host_type ?? '—'}</td>
                      <td className="py-1.5 pr-3 font-mono text-xs">
                        {h.ips.length ? h.ips.join(', ') : '—'}
                      </td>
                      <td className="py-1.5 text-xs text-muted-foreground whitespace-nowrap">
                        {h.last_activity ? relative(h.last_activity) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
